/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SidebarService } from './sidebar/services/sidebar.service';
import { FacNavbarService } from './navbar/navbar.service';
import { LayoutService } from '@services/layout.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AUIPages } from './sidebar/models/nav-item';
import {
  animate,
  query,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { appModuleActions } from '../views/users-management/store/action-types';
import { Store } from '@ngrx/store';
import { AppHttpService } from '@services/app-http.service';
import { UtilService } from '@services/util.service';
import { forkJoin } from 'rxjs';
import { isEmpty } from 'lodash-es';
import {
  FacAlarm,
  DrawerContentService,
  FacQibAuthService,
  AppInfoService,
  UserInfo,
  ProductInfo
} from '@foglight/angular-common';
import { AiAlarmsService, AIOptimizerInputData, AiOptimizerService } from '@foglight/angular-ai-common';

@Component({
  selector: 'fc-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('routingAnimation', [
      transition('* => *', [
        style({ position: 'relative' }),
        query(':enter', [style({ opacity: 0 })], { optional: true }),
        query(
          ':leave',
          [
            style({
              opacity: 1,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
            }),
            animate('0.2s', style({ opacity: 0 })),
          ],
          { optional: true }
        ),
        query(
          ':enter',
          [
            style({
              opacity: 0,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
            }),
            animate('0.2s', style({ opacity: 1 })),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  theme = '';
  theme_content_background = '';
  showSidebar = false;
  pinnedState$;
  loadLayoutPage = false;
  contentWidth = '';

  constructor(
    private sidebarService: SidebarService,
    private navbarService: FacNavbarService,
    private cdRef: ChangeDetectorRef,
    private layoutService: LayoutService,
    private httpService: AppHttpService,
    private store: Store,
    private util: UtilService,
    private aiAlarmsService: AiAlarmsService,
    public drawerContentService: DrawerContentService,
    private qibAuthService: FacQibAuthService,
    private appInfoService: AppInfoService,
    private aiOptimizerService: AiOptimizerService
  ) {
    this.pinnedState$ = this.sidebarService.pinnedState$;
    this.setServerAndUserInfo();
    this.restoreSessionQibData();
  }

  ngOnInit(): void {
    this.getContainerWidth();

    window.addEventListener('message', (e) => {
      let data = e.data;
      if (this.isAlarm(data)) {
        this.openAlarmDetailsDrawer(data);
      }
      else if (this.isAiOptimizerData(data)) {
        this.launchAiOptimizer(data);
      }
    });

    this.navbarService.userSelectedTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.theme = res;
        this.checkTheme();
        this.cdRef.detectChanges();
      });

    this.layoutService.verifyCloudAndSaasAcceptance();

    this.layoutService.showSideBar
      .pipe(takeUntil(this.destroy$))
      .subscribe((showSidebar) => {
        this.showSidebar = showSidebar;
        this.cdRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getContainerWidth(): void {
    const containerElement = document.getElementById('layout') as HTMLElement;
    if (containerElement) {
      this.drawerContentService.watchContentResize(containerElement);
    }

    this.drawerContentService.contentWidth$.pipe(takeUntil(this.destroy$)).subscribe((contentWidth) => {
      this.contentWidth = contentWidth;
    });
  }

  checkTheme(): void {
    let fullPath: string = window.location.href;
    this.theme_content_background = 'light';
    this.theme = localStorage.getItem('theme') || 'light-theme';
    const AUIPagesWithTheme = AUIPages.some((page) => fullPath.includes(page));

    if (fullPath.includes('wcf') || AUIPagesWithTheme) {
      if (this.theme == 'dark-theme') {
        this.theme_content_background = 'dark';
      } else {
        this.theme_content_background = 'light';
      }
    } else {
      this.theme_content_background = 'light';
    }

    this.cdRef.detectChanges();
  }

  isAlarm(obj: any): obj is FacAlarm {
    return typeof obj.alarmId === 'string' && typeof obj.agentId === 'number';
  }

  openAlarmDetailsDrawer(alarmDetails: FacAlarm): void {
    this.aiAlarmsService.launchAiAlarmsDrawer(alarmDetails, this.contentWidth);
  }

  // Check if the message event data is from AI optimizer input data.
  isAiOptimizerData(data: any): boolean {
    return !!(data.sqlId?.trim() && data.sqlFullText?.trim() && data.instance?.trim() && data.domain?.trim());
  }

  launchAiOptimizer(data: any): void {
    const aiOptimizerInput: AIOptimizerInputData = {
      domain: {label: data.domain, value: data.domain, icon: data.domain.toLowerCase()},
      osType: data.osType,
      sqlText: data.sqlFullText,
      isFullSqlText: true,
      executionPlan: data.executionPlan,
      additionalData: {
        'Query ID': data.sqlId,
        Instance: data.instance
      }
    };
    
    this.aiOptimizerService.launchAiOptimizerDrawer(aiOptimizerInput, this.contentWidth);
  }

  unfoldTemporarily(): void {
    this.pinnedState$.next(false);
  }

  foldTemporarily(): void {
    this.pinnedState$.next(true);
  }

  /**
   * Call product and user details API and store in ngRX
   * SaasAddendum Check add for cloud user
   */
  setServerAndUserInfo(): void {
    const serverInfoRequest = this.httpService.getProductInfo();
    const userInfoRequest = this.httpService.getUserInfo();
    forkJoin([serverInfoRequest, userInfoRequest])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        const serverInfo = result[0];
        const userInfo = result[1];
        this.appInfoService.userInfo = userInfo;

        if (!isEmpty(serverInfo)) {
          this.store.dispatch(
            appModuleActions.setServerInfo({ serverInfo: serverInfo })
          );
        }
        if (!isEmpty(userInfo)) {
          this.store.dispatch(
            appModuleActions.setUserInfo({ userInfo: userInfo })
          );
        }

        this.checkSaasAddendumAcceptance(serverInfo, userInfo);
      });
  }

  /**
   * SaasAddendum Acceptance check applicable for cloud env.
   * Check SaasAddendum Acceptance if user if Cloud user else it will true
   */
  checkSaasAddendumAcceptance(productInfo: ProductInfo, userInfo: UserInfo): void {
    if (productInfo.isCloud) {
      if (this.util.isCloudUser(userInfo)) {
        this.httpService
          .getSaasAddendum()
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (user) => {
              if (user.userName) {
                this.storeSaasAddendumAcceptance(true);
              } else {
                this.storeSaasAddendumAcceptance(false);
              }
            },
            (error) => {
              this.storeSaasAddendumAcceptance(false);
            }
          );
      } else {
        this.storeSaasAddendumAcceptance(true);
      }
    } else {
      this.storeSaasAddendumAcceptance(true);
    }
  }

  /**
   * Store saasAccepted flag in store
   * @param saasAccepted
   */
  storeSaasAddendumAcceptance(saasAccepted: boolean): void {
    this.store.dispatch(
      appModuleActions.setSaasAddendumAcceptance({
        saasAccepted,
      })
    );

    this.loadLayoutComponent();
  }

  @HostListener('window:beforeunload')
  @HostListener('window:unload')
  @HostListener('window:pagehide')
  goToPage() {
    this.storeQibDataOnSession();
  }

  storeQibDataOnSession(): void {
    const aiQibDetails: Record<string, unknown> = {};

    if (!isEmpty(this.qibAuthService.qibAccessToken)) {
      aiQibDetails['qibToken'] = this.qibAuthService.qibAccessToken;
    }
    if (!isEmpty(this.qibAuthService.qibRefreshToken)) {
      aiQibDetails['renewToken'] = this.qibAuthService.qibRefreshToken;
    }
    if (!isEmpty(this.qibAuthService.qibId_token)) {
      aiQibDetails['idToken'] = this.qibAuthService.qibId_token;
    }
    if (!isEmpty(this.qibAuthService.state)) {
      aiQibDetails['state'] = this.qibAuthService.state;
    }
    if (!isEmpty(this.appInfoService.aiLicenseDetails)) {
      aiQibDetails['aiLicenseDetails'] = this.appInfoService.aiLicenseDetails;
    }

    // Actually store the data to sessionStorage
    if (Object.keys(aiQibDetails).length > 0) {
      sessionStorage.setItem('aiQibDetails', JSON.stringify(aiQibDetails));
    }
  }

  restoreSessionQibData(): void {
    const aiQibDetails = JSON.parse(sessionStorage.getItem('aiQibDetails') || '{}');

    if (!isEmpty(aiQibDetails?.qibToken)) {
      this.qibAuthService.qibAccessToken = aiQibDetails.qibToken;
    }

    if (!isEmpty(aiQibDetails?.renewToken)) {
      this.qibAuthService.qibRefreshToken = aiQibDetails.renewToken;
    }

    if (!isEmpty(aiQibDetails?.idToken)) {
      this.qibAuthService.qibId_token = aiQibDetails.idToken;
    }

    if (!isEmpty(aiQibDetails?.state)) {
      this.qibAuthService.state = aiQibDetails.state;
    }
    if (!isEmpty(aiQibDetails?.aiLicenseDetails)) {
      this.appInfoService.aiLicenseDetails = aiQibDetails.aiLicenseDetails;
    }
    sessionStorage.removeItem('aiQibDetails');
  }

  /**
   * Allow Layout load once done with essentials app data store
   */
  loadLayoutComponent(): void {
    this.loadLayoutPage = true;
    this.cdRef.detectChanges();
  }
}
